import { AiTrigger, ToolboxContainer } from './styles';
import { ToolboxItem } from './ToolboxItem';
import { TtsContext } from '../../../contexts/TtsContext';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ChatsCircle,
  Equalizer,
  Gauge,
  MagicWand,
  Users,
  WaveSine,
  Brain,
  Pause,
  X,
} from '@phosphor-icons/react';
import { AiContext } from '../../../contexts/AiContext';
import { SelectionRange } from '../EditablePage/EditableBlock';
import { VolumePopup } from './ControlPopup/VolumePopup';
import { PitchPopup } from './ControlPopup/PitchPopup';
import { SpeedPopup } from './ControlPopup/SpeedPopup';
import { StylePopup } from './ControlPopup/StylePopup';
import { BreakPopup } from './ControlPopup/BreakPopup';
import { PronunciationPopup } from './ControlPopup/PronunciationPopup';
import { BlockControl, useStore } from '../../../store';
import { useTranslation } from 'react-i18next';

interface ToolboxProps {
  blockId: string;
  range: SelectionRange | undefined;
  selectionId?: string;
  openSpeakerModal: () => void;
}

export interface IToolboxItem {
  key: string;
  label: string;
  icon: ReactNode;
  popup?: ReactNode;
  openModal?: () => void;
  selection?: SelectionRange;
}

export const Toolbox = ({
  blockId,
  range,
  selectionId,
  openSpeakerModal,
}: ToolboxProps) => {
  const { t } = useTranslation('myapps');

  const [controls, setControls] = useState<BlockControl>();

  const { hideToolbox } = useContext(TtsContext);
  const { showAiTools } = useContext(AiContext);

  const ref = useRef<HTMLDivElement>(null);

  const { blocks } = useStore();

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      const block = blocks.find((block) => block.id === blockId);
      setControls(block?.controls);
    }

    return () => {
      isMounted = false;
    };
  }, [blockId, blocks, range]);

  const getIcon = (control: string): ReactNode => {
    switch (control) {
      case 'speaker':
        return <Users weight="duotone" />;
      case 'speed':
        return <Gauge weight="duotone" />;
      case 'pitch':
        return <WaveSine weight="duotone" />;
      case 'volume':
        return <Equalizer weight="duotone" />;
      case 'style':
        return <MagicWand weight="duotone" />;
      case 'pronunciation':
        return <ChatsCircle weight="duotone" />;
      case 'break':
        return <Pause weight="duotone" />;
      default:
        break;
    }
  };

  const getPopup = useCallback(
    (control: string): ReactNode => {
      switch (control) {
        case 'speed':
          return (
            <SpeedPopup
              blockId={blockId}
              selection={range}
              selectionId={selectionId}
            />
          );
        case 'pitch':
          return <PitchPopup blockId={blockId} />;
        case 'volume':
          return <VolumePopup blockId={blockId} />;
        case 'style':
          return (
            <StylePopup
              blockId={blockId}
              selection={range}
              selectionId={selectionId}
            />
          );
        case 'pronunciation':
          return (
            <PronunciationPopup
              blockId={blockId}
              selection={range}
              selectionId={selectionId}
            />
          );
        case 'break':
          return <BreakPopup blockId={blockId} />;
        default:
          break;
      }
    },
    [blockId, range, selectionId]
  );

  const items: IToolboxItem[] = useMemo(() => {
    if (controls) {
      return Object.keys(controls)
        .map((control) => {
          return {
            key: control,
            label: t(`TTS_TOOLBOX_ITEM_${control.toUpperCase()}`),
            icon: getIcon(control),
            popup: getPopup(control),
            openModal: control === 'speaker' ? openSpeakerModal : undefined,
          };
        })
        .filter((item) =>
          range
            ? item.key !== 'pitch' &&
              item.key !== 'volume' &&
              item.key !== 'speaker' &&
              item.key !== 'break' &&
              item !== undefined
            : item !== undefined && item.key !== 'pronunciation'
        );
    } else {
      return [];
    }
  }, [controls, t, getPopup, openSpeakerModal, range]);

  useEffect(() => {
    let isMounted = true;

    const handleClickOutside = (event: any) => {
      if (
        isMounted &&
        ref.current &&
        !ref.current.contains(event.target) &&
        !document.querySelector('.ms-Modal')?.contains(event.target)
      ) {
        hideToolbox && hideToolbox();
      }
    };

    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      isMounted = false;
    };
  }, [hideToolbox]);

  return (
    <ToolboxContainer ref={ref}>
      {showAiTools && (
        <AiTrigger onClick={() => showAiTools()}>
          <span>{t('TTS_TOOLBOX_AI')}</span>
          <Brain weight="bold" />
          <div className="tag">{t('TTS_TOOLBOX_AI_SOON_TAG')}</div>
        </AiTrigger>
      )}
      {items &&
        items.map((item) => (
          <ToolboxItem
            key={`control-tool-${blockId}${item.key}`}
            control={item}
            blockId={blockId}
          />
        ))}

      {selectionId ? (
        <ToolboxItem
          control={{
            icon: <X />,
            label: t('TTS_TOOLBOX_ITEM_CLEAR'),
            key: 'clear-selections',
          }}
          selectionId={selectionId}
          blockId={blockId}
        />
      ) : (
        !range && (
          <ToolboxItem
            control={{
              icon: <X />,
              label: t('TTS_TOOLBOX_ITEM_CLEAR'),
              key: 'clear-block',
            }}
            blockId={blockId}
          />
        )
      )}
    </ToolboxContainer>
  );
};
